<template>
  <RevList
    class="bg-surface-default-hi shadow-short overflow-hidden rounded-lg"
    :hasExternalBorders="false"
  >
    <RevListItemAction
      v-for="category in categories"
      :key="category.id"
      class="min-h-64 items-center"
      :data-qa="getCategoryDataQA(category.id.toString())"
      :data-test="getCategoryDataQA(category.id.toString())"
      has-chevron
      :to="category.url"
    >
      <template #prefix>
        <component
          :is="FAQ_CATEGORIES_ICONS[category.id] || IconInfo"
          :label="category.name"
          size="24"
        />
      </template>

      <template #label>
        <span class="body-1">
          {{ category.name }}
        </span>
      </template>
    </RevListItemAction>
  </RevList>
</template>

<script setup lang="ts">
import { useRuntimeConfig } from '#imports'

import { RevList } from '@ds/components/List'
import { RevListItemAction } from '@ds/components/ListItemAction'
import { IconAvatar } from '@ds/icons/IconAvatar'
import { IconContact } from '@ds/icons/IconContact'
import { IconCycle } from '@ds/icons/IconCycle'
import { IconInfo } from '@ds/icons/IconInfo'
import { IconMoney } from '@ds/icons/IconMoney'
import { IconQuality } from '@ds/icons/IconQuality'
import { IconRefund } from '@ds/icons/IconRefund'
import { IconShoppingBag } from '@ds/icons/IconShoppingBag'
import { IconSim } from '@ds/icons/IconSim'
import { IconSiren } from '@ds/icons/IconSiren'
import { IconSwap } from '@ds/icons/IconSwap'
import { IconTools } from '@ds/icons/IconTools'
import { IconTruck } from '@ds/icons/IconTruck'

import type { FAQCategory } from '~/api/help-center/types/FaqCategories.types'
import { isConfigDefined } from '~/scopes/care-commons/utils/isConfigDefined'

defineProps<{
  categories: FAQCategory[]
}>()

const config = useRuntimeConfig()

const FAQ_CATEGORIES_ICONS = {
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_ACCOUNT_ID,
    'ZENDESK_CATEGORY_ACCOUNT_ID',
  )]: IconAvatar,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_BUYING_ID,
    'ZENDESK_CATEGORY_BUYING_ID',
  )]: IconShoppingBag,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_CONTACT_AND_SUPPORT_ID,
    'ZENDESK_CATEGORY_CONTACT_AND_SUPPORT_ID',
  )]: IconContact,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_MOBILE_PLAN_ID,
    'ZENDESK_CATEGORY_MOBILE_PLAN_ID',
  )]: IconSim,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_PAYMENTS_ID,
    'ZENDESK_CATEGORY_PAYMENTS_ID',
  )]: IconMoney,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_PROBLEM_WITH_MY_ORDER_ID,
    'ZENDESK_CATEGORY_PROBLEM_WITH_MY_ORDER_ID',
  )]: IconSiren,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_RETURNS_ID,
    'ZENDESK_CATEGORY_RETURNS_ID',
  )]: IconRefund,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_SELLS_ID,
    'ZENDESK_CATEGORY_SELLS_ID',
  )]: IconSwap,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_SHIPPING_ID,
    'ZENDESK_CATEGORY_SHIPPING_ID',
  )]: IconTruck,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_WARRANTY_ID,
    'ZENDESK_CATEGORY_WARRANTY_ID',
  )]: IconQuality,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_TROUBLESHOOTING_ID,
    'ZENDESK_CATEGORY_TROUBLESHOOTING_ID',
  )]: IconTools,
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_RECYCLE_ID,
    'ZENDESK_CATEGORY_RECYCLE_ID',
  )]: IconCycle,
} as const

const FAQ_CATEGORIES_DATA_ID = {
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_ACCOUNT_ID,
    'ZENDESK_CATEGORY_ACCOUNT_ID',
  )]: 'faq-category-account',
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_BUYING_ID,
    'ZENDESK_CATEGORY_BUYING_ID',
  )]: 'faq-category-buying',
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_MOBILE_PLAN_ID,
    'ZENDESK_CATEGORY_MOBILE_PLAN_ID',
  )]: 'faq-category-mobile-plan',
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_PAYMENTS_ID,
    'ZENDESK_CATEGORY_PAYMENTS_ID',
  )]: 'faq-category-payments',
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_RETURNS_ID,
    'ZENDESK_CATEGORY_RETURNS_ID',
  )]: 'faq-category-returns',
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_SELLS_ID,
    'ZENDESK_CATEGORY_SELLS_ID',
  )]: 'faq-category-sells',
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_SHIPPING_ID,
    'ZENDESK_CATEGORY_SHIPPING_ID',
  )]: 'faq-category-shipping',
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_WARRANTY_ID,
    'ZENDESK_CATEGORY_WARRANTY_ID',
  )]: 'faq-category-warranty',
  [isConfigDefined(
    config.public.ZENDESK_CATEGORY_RECYCLE_ID,
    'ZENDESK_CATEGORY_RECYCLE_ID',
  )]: 'faq-category-recycle',
} as const

function getCategoryDataQA(categoryId: string) {
  return FAQ_CATEGORIES_DATA_ID[categoryId] || `faq-category-${categoryId}`
}
</script>
